import React, { useCallback, useEffect, useMemo, useState, createElement } from 'react';
import { supabase } from '../../lib/supabase';
import { formatCurrency } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Skeleton } from '../ui/Skeleton';
import { MetricsDateFilter } from './MetricsDateFilter';
import { Banknote, CreditCard, Smartphone, Download, RefreshCw, Receipt, Utensils, Bike, ShoppingBag, Ban, AlertCircle } from 'lucide-react';
// ──────────────────────────────────────────────────────────────────
// CashClose — La Cabaña Parrillera + NatuCafé + Emilius
// Renders a per-product sales log for a chosen date range, with
// timestamp, price, payment method and channel (mesa / delivery /
// pickup). Reads from the `payments` table (items_json snapshot)
// joined with `orders` for delivery_type. Read-only — does NOT mutate
// any data.
//
// El componente tiene su PROPIO filtro de rango interno (mismos
// presets que MetricsDateFilter: Hoy / Ayer / Esta Semana / Este Mes
// / Último Mes / 3 Meses + Desde/Hasta). Es INDEPENDIENTE del filtro
// global de MetricsPage — cumplen funciones distintas.
// ──────────────────────────────────────────────────────────────────
interface CabanaCashCloseProps {
  restaurantId: number | string;
}
interface PaymentRow {
  id: string;
  restaurant_id: number;
  order_id: string;
  table_id: string | null;
  amount: number;
  method: string;
  items_json: any[];
  created_at: string;
  is_partial: boolean;
}
interface OrderMeta {
  id: string;
  delivery_type: string | null;
  table_id: number | null;
  order_number: string | null;
  // Express / domicilio fee charged to this order. Lives on the order
  // itself (not on the payment row's items_json), so we must surface it
  // separately to avoid undercounting the day's revenue.
  delivery_fee: number;
  // Authoritative order total from `orders.total`. Used as the ceiling
  // when flattening payments into per-item rows so the cash close
  // always matches Dashboard / `v_sales_summary_today` exactly,
  // regardless of what was written to payments.items_json.
  order_total: number;
}
interface FlatRow {
  payment_id: string;
  order_id: string;
  order_number: string | null;
  created_at: string;
  method: string;
  channel: 'mesa' | 'delivery' | 'pickup' | 'desconocido';
  table_id: number | null;
  /** Human-readable table label (e.g. "Mesa 7" or "Barra 2"). Null if not a mesa order. */
  table_label: string | null;
  item_name: string;
  quantity: number;
  unit_price: number;
  line_total: number;
  notes: string | null;
}
interface VoidLog {
  id: number;
  restaurant_id: number;
  order_id: number | null;
  item_id: number | null;
  action_type: string;
  authorized_name: string;
  reason: string;
  amount: number;
  detail: string | null;
  created_at: string;
}
const todayIso = () => {
  // Fecha calendario de COSTA RICA (UTC-6), NO la del navegador. Si usáramos
  // d.getFullYear()/getDate() tomaríamos el día local del dispositivo: cuando
  // la PC está en otra zona, cerca de medianoche el día local ≠ día CR y la
  // ventana del cierre (que ya usa -06:00) se armaría sobre el día equivocado.
  // `en-CA` formatea directamente como YYYY-MM-DD.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Costa_Rica',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(new Date());
};
const methodMeta = (m: string) => {
  switch (m) {
    case 'cash':
      return {
        label: 'Efectivo',
        Icon: Banknote,
        chip: 'bg-emerald-50 text-emerald-700 border-emerald-200'
      };
    case 'sinpe':
      return {
        label: 'SINPE',
        Icon: Smartphone,
        chip: 'bg-blue-50 text-blue-700 border-blue-200'
      };
    case 'card':
    case 'tarjeta':
      return {
        label: 'Tarjeta',
        Icon: CreditCard,
        chip: 'bg-violet-50 text-violet-700 border-violet-200'
      };
    case 'transfer':
    case 'transferencia':
      return {
        label: 'Transferencia',
        Icon: Smartphone,
        chip: 'bg-blue-50 text-blue-700 border-blue-200'
      };
    case 'unknown':
    case '':
    case 'sin_metodo':
      return {
        label: 'Sin método',
        Icon: Receipt,
        chip: 'bg-gray-100 text-gray-700 border-gray-300'
      };
    default:
      return {
        label: m,
        Icon: Banknote,
        chip: 'bg-gray-50 text-gray-700 border-gray-200'
      };
  }
};
const channelMeta = (c: FlatRow['channel'], tableLabel: string | null) => {
  switch (c) {
    case 'mesa':
      return {
        label: tableLabel || 'Mesa',
        Icon: Utensils,
        chip: 'bg-amber-50 text-amber-700 border-amber-200'
      };
    case 'delivery':
      return {
        label: 'Delivery',
        Icon: Bike,
        chip: 'bg-cyan-50 text-cyan-700 border-cyan-200'
      };
    case 'pickup':
      return {
        label: 'Para llevar',
        Icon: ShoppingBag,
        chip: 'bg-orange-50 text-orange-700 border-orange-200'
      };
    default:
      return {
        label: '—',
        Icon: Receipt,
        chip: 'bg-gray-50 text-gray-600 border-gray-200'
      };
  }
};
const fmtTime = (iso: string) => {
  try {
    return new Date(iso).toLocaleTimeString('es-CR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  } catch {
    return iso;
  }
};
const fmtFull = (iso: string) => {
  try {
    return new Date(iso).toLocaleString('es-CR', {
      dateStyle: 'short',
      timeStyle: 'medium'
    });
  } catch {
    return iso;
  }
};
// Fecha y hora SIEMPRE en horario Costa Rica (America/Costa_Rica), para que
// al revisar rangos de varios días cada anulación muestre el día correcto
// sin depender de la zona horaria del navegador del cajero.
const fmtDateCR = (iso: string) => {
  try {
    return new Date(iso).toLocaleDateString('es-CR', {
      timeZone: 'America/Costa_Rica',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch {
    return iso;
  }
};
const fmtTimeCR = (iso: string) => {
  try {
    return new Date(iso).toLocaleTimeString('es-CR', {
      timeZone: 'America/Costa_Rica',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  } catch {
    return iso;
  }
};
// ════════════════════════════════════════════════════════════════════
// getCashCloseTotals — ÚNICA FUENTE DE VERDAD del Cierre de Caja.
// Calcula TODO exclusivamente desde payments.amount. NO usa items_json,
// orders.total, flatRows, ni el status de la orden. Es una función pura:
// recibe los payments ya cargados y devuelve el total del día + el
// desglose por método. Las tarjetas del visual leen ÚNICAMENTE de aquí.
// ════════════════════════════════════════════════════════════════════
export interface CashCloseTotals {
  totalDelDia: number;
  byMethod: Record<string, {
    total: number;
    count: number;
  }>;
  paymentsCount: number;
  paymentIds: Array<[string, number, string, string]>;
}
export function getCashCloseTotals(payments: PaymentRow[]): CashCloseTotals {
  const byMethod: Record<string, {
    total: number;
    count: number;
  }> = {
    cash: {
      total: 0,
      count: 0
    },
    card: {
      total: 0,
      count: 0
    },
    sinpe: {
      total: 0,
      count: 0
    }
  };
  let totalDelDia = 0;
  const paymentIds: Array<[string, number, string, string]> = [];
  for (const p of payments) {
    const amt = Number(p.amount) || 0;
    totalDelDia += amt;
    const m = (p.method || 'sin_metodo') as string;
    if (!byMethod[m]) byMethod[m] = {
      total: 0,
      count: 0
    };
    byMethod[m].total += amt;
    byMethod[m].count += 1;
    paymentIds.push([String(p.id), amt, m, String(p.created_at)]);
  }
  return {
    totalDelDia,
    byMethod,
    paymentsCount: payments.length,
    paymentIds
  };
}
export function CabanaCashClose({
  restaurantId
}: CabanaCashCloseProps) {
  // ── Filtro de rango interno. Default: hoy. Controlado por el mismo
  // <MetricsDateFilter> que usa el filtro global de MetricsPage para
  // mantener UI consistente, pero su estado vive AQUÍ — totalmente
  // independiente del filtro de arriba.
  const [startDate, setStartDate] = useState<string>(todayIso());
  const [endDate, setEndDate] = useState<string>(todayIso());
  // Rango "es hoy" → start=end=hoy. Usado para auto-refresh y copy.
  const rangeIsToday = startDate === todayIso() && endDate === todayIso();
  const isSingleDay = startDate === endDate;
  const [payments, setPayments] = useState<PaymentRow[]>([]);
  const [orderMeta, setOrderMeta] = useState<Record<string, OrderMeta>>({});
  const [voidLogs, setVoidLogs] = useState<VoidLog[]>([]);
  /** Maps restaurant_tables.id → human label (e.g. "Mesa 7" / "Barra 2"). */
  const [tableLabels, setTableLabels] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    setError(null);
    try {
      // Build the day window anchored to COSTA RICA time (UTC-6, fixed —
      // Costa Rica has no daylight saving). We must NOT use the browser's
      // local timezone: if the cashier's machine is on any other zone,
      // local midnight ≠ CR midnight and payments near the day boundary
      // fall outside the window (that is what dropped a payment from the
      // cash close). The explicit `-06:00` offset pins both edges to the
      // real CR calendar day regardless of the device timezone.
      const start = new Date(`${startDate}T00:00:00-06:00`);
      const end = new Date(`${endDate}T23:59:59.999-06:00`);
      // ─── (0) Load void logs (All restaurants) ─────────────────────────
      try {
        const {
          data: voids,
          error: voidErr
        } = await supabase.from('void_audit_log').select('*').eq('restaurant_id', Number(restaurantId)).gte('created_at', start.toISOString()).lte('created_at', end.toISOString()).order('created_at', {
          ascending: false
        });
        if (!voidErr && voids) {
          setVoidLogs(voids as VoidLog[]);
        }
      } catch (e) {
        console.error('[CabanaCashClose] error loading void logs:', e);
      }
      // ─── (1) Load payments rows for the day ─────────────────────────
      // These are the "official" cash close rows generated by the
      // Cobrar / Facturar flow (processPayment).
      const {
        data: pays,
        error: payErr
      } = await supabase.from('payments').select('id, restaurant_id, order_id, table_id, amount, method, items_json, created_at, is_partial').eq('restaurant_id', Number(restaurantId)).gte('created_at', start.toISOString()).lte('created_at', end.toISOString()).order('created_at', {
        ascending: true
      });
      if (payErr) throw payErr;
      const safePays = (pays || []) as PaymentRow[];
      // ─── (2) Load ALL closed/paid orders for the day ───────────────
      // Captures orders closed by ANY path:
      //   • "Cobrar / Facturar" → status=delivered + payment_method set
      //   • "Entregar al cliente" (Caja) → status=delivered
      //   • Dashboard / marketplace fulfillment → status=delivered|completed
      //   • Any order where a method was registered → payment_method != null
      //
      // We filter by `created_at` (immutable) instead of `updated_at`
      // (mutates on every touch) so historical days are stable and
      // reproducible. Dine-in orders for La Cabaña are created and closed
      // the same day, which makes this the natural mental model.
      const {
        data: deliveredOrdersRaw,
        error: delErr
      } = await supabase.from('orders').select('id, order_number, delivery_type, table_id, payment_method, total, status, delivered_at, updated_at, created_at').eq('restaurant_id', Number(restaurantId)).gte('created_at', start.toISOString()).lte('created_at', end.toISOString()).or('status.eq.delivered,status.eq.completed,status.eq.paid,payment_method.not.is.null');
      if (delErr) throw delErr;
      // Statuses that DISQUALIFY an order from the cash close, no matter
      // what payment_method is set on the row.
      const EXCLUDED_STATUSES = new Set(['cancelled', 'canceled', 'cancelado', 'refunded', 'reembolsado', 'voided', 'rejected']);
      // ─── CASH CLOSE = payments.amount as the SINGLE SOURCE OF TRUTH ──
      // Regla (todos los restaurantes): TOTAL DEL DÍA = SUM(payments.amount)
      // de los pagos NO cancelados del día (por payments.created_at en hora
      // local). NO exigimos que la orden esté delivered/completed/paid/etc;
      // NO usamos orders.total; NO reconciliamos hacia orders.total; NO
      // sintetizamos filas ni rellenamos "Otros cargos". Sólo excluimos un
      // pago cuando su orden está cancelada / anulada / reembolsada. Las
      // mesas abiertas con pagos parciales cuentan el día que entró el pago.
      const isCancelled = (o: any) => EXCLUDED_STATUSES.has(String(o?.status || '').toLowerCase().trim());
      // Order ids we need metadata for (channel / table label): every order
      // with a payment in the window, plus orders caught by the paid-items
      // safety net.
      const paymentOrderIds = Array.from(new Set(safePays.map(p => String(p.order_id)))).filter(Boolean);
      const deliveredOrderIds = Array.from(new Set((deliveredOrdersRaw || []).map((o: any) => String(o.id))));
      // ⚠️ El cierre de caja se ancla SIEMPRE a `orders.created_at` (la fecha
      // en que entró el pedido), nunca a `paid_at` ni `updated_at`. Por eso
      // NO usamos el viejo "safety net" por `order_items.paid_at`: ese traía
      // órdenes creadas otro día (p. ej. creadas el 12/06 y tocadas el 13/06)
      // y las contaba en el día equivocado. Se deja vacío a propósito.
      const extraPaidOrderIds: string[] = [];
      const allOrderIds = Array.from(new Set([...paymentOrderIds, ...deliveredOrderIds, ...extraPaidOrderIds]));
      // Fetch metadata for any order id not already in the raw set. This
      // ALSO pulls OPEN tables that have a partial payment but a
      // non-revenue status, so their payments still count today (regla 9).
      const missingMetaIds = allOrderIds.filter(id => !deliveredOrderIds.includes(id));
      let extraOrders: any[] = [];
      if (missingMetaIds.length > 0) {
        // 🔒 MULTI-TENANT SAFETY (defense in depth): .eq('restaurant_id')
        // guarantees the result set is strictly this tenant's data.
        const {
          data: extra,
          error: exErr
        } = await supabase.from('orders').select('id, order_number, delivery_type, table_id, payment_method, total, status, delivered_at, updated_at, created_at, restaurant_id').eq('restaurant_id', Number(restaurantId)).in('id', missingMetaIds);
        if (exErr) throw exErr;
        extraOrders = extra || [];
      }
      const allOrders = [...(deliveredOrdersRaw || []), ...extraOrders];
      // Cancelled / voided / refunded order ids — the ONLY thing that
      // disqualifies a payment from the cash close.
      const cancelledOrderIds = new Set<string>(allOrders.filter(isCancelled).map((o: any) => String(o.id)));
      // ─── FECHA BASE DEL CIERRE = orders.created_at ───────────────────
      // Cada cobro se atribuye al día en que se CREÓ el pedido, no al día
      // en que se registró el pago. Construimos el set de órdenes cuyo
      // created_at cae dentro de la ventana CR [start, end]. Así un pedido
      // creado el 12/06 y pagado/tocado el 13/06 NO cuenta el 13/06.
      const startMs = start.getTime();
      const endMs = end.getTime();
      const orderCreatedInWindowIds = new Set<string>(allOrders.filter((o: any) => {
        const t = o?.created_at ? new Date(o.created_at).getTime() : NaN;
        return Number.isFinite(t) && t >= startMs && t <= endMs;
      }).map((o: any) => String(o.id)));
      // Conjunto de ids de órdenes conocidas (para distinguir "fuera de
      // ventana" de "orden desconocida").
      const knownOrderIds = new Set<string>(allOrders.map((o: any) => String(o.id)));
      // Conservamos un pago cuando: (a) su orden NO está cancelada, y (b)
      // su orden fue creada dentro de la ventana. Si la orden NO se conoce
      // (no vino en la consulta), conservamos el pago (dinero real ya
      // cobrado) para no perder cobros por metadata faltante.
      const filteredSafePays = safePays.filter(p => {
        const oid = String(p.order_id);
        if (cancelledOrderIds.has(oid)) return false;
        if (knownOrderIds.has(oid) && !orderCreatedInWindowIds.has(oid)) {
          return false;
        }
        return true;
      });
      // ─── FALLBACK histórico: órdenes cerradas SIN fila en `payments` ──
      // El cierre suma SUM(payments.amount), pero hay órdenes (sobre todo de
      // días/meses anteriores) que se cerraron por Caja / Dashboard / Mesas
      // sin generar una fila en `payments`. Para esas, su ingreso real vive
      // en `orders.total`. Sin este respaldo, rangos como "Este Mes" o
      // "Último Mes" mostraban ₡0 aunque sí hubo ventas registradas.
      //
      // Regla: sólo sintetizamos un "pago" desde la orden cuando
      //   • la orden está en deliveredOrdersRaw (entregada/pagada/completada
      //     o con payment_method seteado),
      //   • NO está cancelada / anulada / reembolsada,
      //   • NO tiene NINGUNA fila real en `payments` (si la tiene, ya se
      //     contó arriba — así los días que ya funcionan no cambian nada),
      //   • su total es > 0.
      // El monto sintetizado usa orders.total y su payment_method (cash /
      // sinpe / card). created_at de la orden mantiene el orden cronológico.
      const orderIdsWithPayment = new Set(safePays.map(p => String(p.order_id)));
      const syntheticPays: PaymentRow[] = (deliveredOrdersRaw || []).filter((o: any) => {
        const oid = String(o.id);
        if (cancelledOrderIds.has(oid)) return false;
        if (orderIdsWithPayment.has(oid)) return false;
        return (Number(o.total) || 0) > 0;
      }).map((o: any) => {
        const rawMethod = String(o.payment_method || '').toLowerCase().trim();
        return {
          id: `order:${o.id}`,
          restaurant_id: Number(restaurantId),
          order_id: String(o.id),
          table_id: o.table_id != null ? String(o.table_id) : null,
          amount: Number(o.total) || 0,
          method: rawMethod || 'sin_metodo',
          items_json: [],
          created_at: String(o.created_at),
          is_partial: false
        } as PaymentRow;
      });
      // Build channel / table metadata for EVERY order (incl. canceladas),
      // para que el desglose por canal de esos pagos también cuadre con el
      // total. El status ya NO excluye el pago del cierre.
      const metaMap: Record<string, OrderMeta> = {};
      allOrders.forEach((o: any) => {
        metaMap[String(o.id)] = {
          id: String(o.id),
          delivery_type: o.delivery_type,
          table_id: o.table_id != null ? Number(o.table_id) : null,
          order_number: o.order_number || null,
          delivery_fee: Math.max(0, Number(o.delivery_fee) || 0),
          order_total: Math.max(0, Number(o.total) || 0)
        };
      });
      setOrderMeta(metaMap);
      // ─── TOTAL DEL DÍA = SOLO payments de órdenes NO canceladas ──────
      // Regla del cierre: un pago cuya orden está cancelada / anulada /
      // reembolsada / voided / rejected NO cuenta en la caja del día. Por
      // eso alimentamos `payments` (la fuente que consume getCashCloseTotals,
      // byMethod y byChannel) con `filteredSafePays` — los pagos ya filtrados
      // contra orders.status no cancelado — NO con `safePays` crudo.
      //
      // Las órdenes canceladas se muestran SOLO como auditoría en la sección
      // "Anulaciones y eliminaciones del turno"; nunca se suman al total.
      // Ej. 08/06/2026: ₡92.650 bruto − ₡4.800 cancelado = ₡87.850 válido.
      const merged = [...filteredSafePays, ...syntheticPays].sort((a, b) => a.created_at.localeCompare(b.created_at));
      setPayments(merged);
      // ─── (8) Resolve table_id (FK) → label for every table referenced ─
      const tableIds = Array.from(new Set(allOrders.map((o: any) => o.table_id).filter((v: any) => v != null).map((v: any) => Number(v)).concat(safePays.map(p => p.table_id != null ? Number(p.table_id) : null).filter((v): v is number => v != null))));
      if (tableIds.length > 0) {
        // El schema real de producción guarda las mesas en la tabla
        // `tables` (la FK orders.table_id → tables.id), NO en
        // `restaurant_tables` — esta última no tiene la columna
        // `table_number`, lo que provocaba el error 400 repetido en
        // consola ("column restaurant_tables.table_number does not exist").
        // `tables` sí expone id / table_number / table_name.
        const {
          data: tablesData,
          error: tblErr
        } = await supabase.from('tables').select('id, table_number, table_name').in('id', tableIds);
        if (tblErr) {
          console.warn('[CabanaCashClose] could not resolve table labels', tblErr);
          setTableLabels({});
        } else {
          const tMap: Record<number, string> = {};
          (tablesData || []).forEach((t: any) => {
            const label = t.table_name && String(t.table_name).trim() || (t.table_number != null ? `Mesa ${t.table_number}` : `Mesa ${t.id}`);
            tMap[Number(t.id)] = label;
          });
          setTableLabels(tMap);
        }
      } else {
        setTableLabels({});
      }
    } catch (err: any) {
      console.error('[CabanaCashClose] load error:', err);
      setError(err?.message || 'Error cargando ventas del día.');
    } finally {
      setLoading(false);
    }
  }, [restaurantId, startDate, endDate]);
  useEffect(() => {
    load();
  }, [load]);
  // ─── Auto-refresh: only when the range is "today only" ───
  // Refreshes every 20s so newly processed payments appear without
  // forcing the user to click ⟳. Past dates / multi-day ranges never
  // auto-refresh.
  useEffect(() => {
    if (!rangeIsToday) return;
    const id = setInterval(() => {
      if (!loading) load();
    }, 20000);
    return () => clearInterval(id);
  }, [rangeIsToday, loading, load]);
  // Flatten payments → one row per item sold.
  //
  // KEY INVARIANT: for every revenue-valid order, the sum of its rows
  // equals `orders.total`. This is what makes the cash close match
  // Dashboard / `v_sales_summary_today` exactly. If items_json doesn't
  // already sum to the order total (e.g. delivery fee, tip, or any other
  // charge not embedded in items_json), we add a single reconciliation
  // row at the end so the math always closes.
  const flatRows: FlatRow[] = useMemo(() => {
    const rows: FlatRow[] = [];
    // Detalle por ítem para la tabla. SIN reconciliación y SIN relleno de
    // "Otros cargos": el TOTAL DEL DÍA se calcula aparte desde
    // payments.amount (ver useMemo `totals`). Esta tabla es sólo el log
    // de productos vendidos. Si un pago no trae desglose de ítems, se
    // muestra una línea única con su monto cobrado (payment.amount), de
    // modo que cada cobro siempre aparece en el log aunque no tenga items.
    for (const p of payments) {
      const meta = orderMeta[p.order_id];
      let channel: FlatRow['channel'] = 'desconocido';
      if (meta?.delivery_type === 'delivery') channel = 'delivery';else if (meta?.delivery_type === 'pickup') channel = 'pickup';else if (meta?.delivery_type === 'dine_in' || meta?.table_id != null) channel = 'mesa';else if (p.table_id != null) channel = 'mesa';
      // Resolve the human-readable mesa label using either the order's
      // table_id or the payment's table_id (whichever exists).
      const rawTableId = meta?.table_id ?? (p.table_id != null ? Number(p.table_id) : null);
      const resolvedLabel = rawTableId != null ? tableLabels[Number(rawTableId)] || null : null;
      const items = Array.isArray(p.items_json) ? p.items_json : [];
      if (items.length === 0) {
        // No item breakdown — show the payment's amount as a single line.
        const amt = Number(p.amount) || 0;
        rows.push({
          payment_id: p.id,
          order_id: p.order_id,
          order_number: meta?.order_number || null,
          created_at: p.created_at,
          method: p.method,
          channel,
          table_id: rawTableId,
          table_label: resolvedLabel,
          item_name: '(pago sin desglose)',
          quantity: 1,
          unit_price: amt,
          line_total: amt,
          notes: null
        });
      } else {
        for (const it of items) {
          const lineTotal = Number(it.line_total) || 0;
          rows.push({
            payment_id: p.id,
            order_id: p.order_id,
            order_number: meta?.order_number || null,
            created_at: p.created_at,
            method: p.method,
            channel,
            table_id: rawTableId,
            table_label: resolvedLabel,
            item_name: it.item_name || '—',
            quantity: Number(it.quantity) || 1,
            unit_price: Number(it.unit_price) || 0,
            line_total: lineTotal,
            notes: it.notes || null
          });
        }
      }
    }
    return rows;
  }, [payments, orderMeta, tableLabels]);
  // Totals
  const totals = useMemo(() => {
    // byMethod + total del día salen de getCashCloseTotals → la ÚNICA
    // fuente de verdad (SUM(payments.amount)). No se calculan aquí.
    const cc = getCashCloseTotals(payments);
    const byChannel: Record<string, {
      total: number;
      count: number;
    }> = {
      mesa: {
        total: 0,
        count: 0
      },
      delivery: {
        total: 0,
        count: 0
      },
      pickup: {
        total: 0,
        count: 0
      },
      desconocido: {
        total: 0,
        count: 0
      }
    };
    // El TOTAL DEL DÍA y el desglose por método vienen EXCLUSIVAMENTE de
    // getCashCloseTotals(payments) = SUM(payments.amount). El byChannel
    // necesita la metadata de la orden (mesa/delivery/pickup), así que se
    // acumula aquí sobre el MISMO payments.amount — no afecta ni el total
    // ni las tarjetas Efectivo/Tarjeta/SINPE.
    const grand = cc.totalDelDia;
    for (const p of payments) {
      const amt = Number(p.amount) || 0;
      const meta = orderMeta[p.order_id];
      let channel: FlatRow['channel'] = 'desconocido';
      if (meta?.delivery_type === 'delivery') channel = 'delivery';else if (meta?.delivery_type === 'pickup') channel = 'pickup';else if (meta?.delivery_type === 'dine_in' || meta?.table_id != null) channel = 'mesa';else if (p.table_id != null) channel = 'mesa';
      byChannel[channel].total += amt;
      byChannel[channel].count += 1;
    }
    // itemCount es sólo informativo (cantidad de ítems en el log).
    const itemCount = flatRows.reduce((s, r) => s + r.quantity, 0);
    return {
      byMethod: cc.byMethod,
      byChannel,
      grand,
      itemCount,
      paymentCount: cc.paymentsCount
    };
  }, [payments, orderMeta, flatRows]);
  const handleExportCsv = () => {
    const headers = ['Fecha y hora', 'Pedido', 'Producto', 'Cantidad', 'Precio unitario', 'Total línea', 'Método de pago', 'Canal', 'Notas'];
    const escape = (v: any) => {
      const s = String(v ?? '');
      if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const lines = [headers.join(',')];
    for (const r of flatRows) {
      lines.push([escape(fmtFull(r.created_at)), escape(r.order_number || r.order_id.slice(0, 8)), escape(r.item_name), escape(r.quantity), escape(r.unit_price), escape(r.line_total), escape(methodMeta(r.method).label), escape(channelMeta(r.channel, r.table_label).label), escape(r.notes || '')].join(','));
    }
    // Append totals
    lines.push('');
    lines.push(`TOTAL,,,,,${totals.grand},,,`);
    const csv = lines.join('\n');
    const blob = new Blob([`\uFEFF${csv}`], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cierre-caja-la-cabana_${startDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  return <Card className="border-amber-200" data-id="element-348" data-mp="3optcljq0pmph">
      <CardHeader className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3" data-id="element-349" data-mp="3ooqnnhc99uc7">
        <div className="flex items-center gap-3" data-id="element-350" data-mp="6ar5mm015eqt">
          <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center" data-id="element-351" data-mp="6dn1m4sbhtcm">
            <Receipt className="w-5 h-5 text-amber-600" data-id="element-352" data-mp="2cl1llqktd9l" />
          </div>
          <div data-id="element-353" data-mp="gahxefx7hfn7">
            <CardTitle className="text-lg" data-id="element-354" data-mp="g7vuitl9fobb">Cierre de Caja</CardTitle>
            <p className="text-xs text-gray-500 mt-0.5" data-id="element-355" data-mp="bc8285bhef1l">
              Ventas detalladas {isSingleDay ? 'del día' : 'del rango'} con
              producto, precio, método de pago y canal.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-id="element-356" data-mp="3vtog1torsdxm">
          <button onClick={() => load()} disabled={loading} className="h-9 w-9 rounded-lg flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors disabled:opacity-50 relative" title={rangeIsToday ? 'Refrescar (auto cada 20s)' : 'Refrescar'} data-id="element-357" data-mp="3qvegz7zxct6q">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} data-id="element-358" data-mp="3c5ytc0vox3v9" />
            {rangeIsToday && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-500 ring-2 ring-white" data-id="element-359" data-mp="3373oot8ao1u2" />}
          </button>
          <Button variant="secondary" size="sm" onClick={handleExportCsv} disabled={loading || flatRows.length === 0} data-id="element-360" data-mp="2y6peb4j80fsv">
            <Download className="w-4 h-4 mr-2" data-id="element-361" data-mp="2j5s8dbezea9c" />
            CSV
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-5" data-id="element-362" data-mp="2o2sh5u37tr45">
        {/* ── Filtro de rango INTERNO (independiente del filtro global
            de MetricsPage). Mismos chips: Hoy / Ayer / Esta Semana /
            Este Mes / Último Mes / 3 Meses + Desde/Hasta. ── */}
        <div className="rounded-xl border border-amber-100 bg-amber-50/40 p-3" data-id="element-363" data-mp="20ej178sonxkx">
          <MetricsDateFilter startDate={startDate} endDate={endDate} onChange={(s, e) => {
          setStartDate(s);
          setEndDate(e);
        }} data-id="element-364" data-mp="20ceny08w9m7z" />
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-365" data-mp="2f4gsvyptn5ev">
          <div className="rounded-xl border border-gray-100 bg-gradient-to-br from-amber-50 to-white p-3" data-id="element-366" data-mp="1ho1d5hispq5w">
            <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wider" data-id="element-367" data-mp="1mmuq98u67f5j">
              Total del día
            </p>
            <p className="text-2xl font-black text-gray-900 tabular-nums mt-1" data-id="element-368" data-mp="1el3turh3nuqc">
              {formatCurrency(totals.grand)}
            </p>
            <p className="text-[10px] text-gray-500 mt-0.5" data-id="element-369" data-mp="19lhfuftg6agh">
              {totals.paymentCount} cobro
              {totals.paymentCount === 1 ? '' : 's'} · {totals.itemCount} ítem
              {totals.itemCount === 1 ? '' : 's'}
            </p>
          </div>
          {(['cash', 'sinpe', 'card'] as const).map(m => {
          const meta = methodMeta(m);
          const t = totals.byMethod[m] || {
            total: 0,
            count: 0
          };
          return <div key={m} className="rounded-xl border border-gray-100 bg-white p-3" data-id="element-370" data-mp="15jdrwrplddeb">
                <div className="flex items-center gap-1.5" data-id="element-371" data-mp="2h0ds09i6kwiw">
                  <meta.Icon className="w-3.5 h-3.5 text-gray-500" data-id="element-372" data-mp="2h2zy04pfb3z0" />
                  <p className="text-[10px] font-bold text-gray-600 uppercase tracking-wider" data-id="element-373" data-mp="2c10zb84mov8e">
                    {meta.label}
                  </p>
                </div>
                <p className="text-xl font-black text-gray-900 tabular-nums mt-1" data-id="element-374" data-mp="2okogqjeqk3vh">
                  {formatCurrency(t.total)}
                </p>
                <p className="text-[10px] text-gray-500 mt-0.5" data-id="element-375" data-mp="2amgepx2uecdg">
                  {t.count} ítem{t.count === 1 ? '' : 's'}
                </p>
              </div>;
        })}
        </div>

        {/* Channel breakdown */}
        <div className="flex flex-wrap gap-2" data-id="element-376" data-mp="37amm2rr5fivf">
          {(['mesa', 'delivery', 'pickup'] as const).map(c => {
          const meta = channelMeta(c, null);
          const t = totals.byChannel[c] || {
            total: 0,
            count: 0
          };
          return <div key={c} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-bold ${meta.chip}`} data-id="element-377" data-mp="3laxfa1fxl9jr">
                <meta.Icon className="w-3.5 h-3.5" data-id="element-378" data-mp="3q3i74xfa1ckn" />
                <span data-id="element-379" data-mp="22e3tdpjahp9h">{meta.label}</span>
                <span className="tabular-nums" data-id="element-380" data-mp="22azgm3rrdl77">{formatCurrency(t.total)}</span>
                <span className="opacity-60" data-id="element-381" data-mp="1xcegs5gf53ja">· {t.count}</span>
              </div>;
        })}
        </div>

        {/* Table */}
        {error ? <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-id="element-382" data-mp="1odsz0cirtsjk">
            {error}
          </div> : loading ? <div className="space-y-2" data-id="element-383" data-mp="2l5o7xnml7x3w">
            {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-10 w-full rounded-lg" data-id="element-384" data-mp="2g50jn9pqtudd" />)}
          </div> : flatRows.length === 0 ? <div className="text-center py-10 px-4 text-gray-500 text-sm border border-dashed border-gray-200 rounded-xl" data-id="element-385" data-mp="27c5a43t8tchs">
            <p className="font-medium text-gray-700" data-id="element-386" data-mp="33ul642l3v9re">
              No hay ventas cerradas registradas para{' '}
              {isSingleDay ? 'esta fecha' : 'el rango'}.
            </p>
            <p className="text-xs text-gray-500 mt-2 max-w-md mx-auto leading-relaxed" data-id="element-387" data-mp="lkyp1ehbpz9h">
              Aparecen automáticamente las órdenes cobradas o entregadas desde
              Caja, Dashboard, Mesas y cualquier otro flujo. Si no ves datos de
              un día anterior, verifica que esas órdenes tengan estado entregado
              o método de pago registrado.
            </p>
            {rangeIsToday && <p className="text-[11px] text-emerald-700 mt-3 inline-flex items-center gap-1.5" data-id="element-388" data-mp="14kck5zqshg1k">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-389" data-mp="19hujqzix2iww" />
                Actualización automática activa
              </p>}
          </div> : <div className="overflow-x-auto -mx-6" data-id="element-390" data-mp="1iapqjxo18mos">
            <table className="w-full text-sm text-left min-w-[760px]" data-id="element-391" data-mp="1ia6akmhy08de">
              <thead className="text-[11px] text-gray-500 uppercase bg-gray-50" data-id="element-392" data-mp="1nb3scxz1j0ed">
                <tr data-id="element-393" data-mp="1s8m3mv0j5rpj">
                  <th className="px-6 py-2.5 rounded-l-lg" data-id="element-394" data-mp="1sa6ua9jq4qi4">Hora</th>
                  <th className="px-3 py-2.5" data-id="element-395" data-mp="3oc0hjo9h7x0i">Pedido</th>
                  <th className="px-3 py-2.5" data-id="element-396" data-mp="3ocjqifh5fb2e">Producto</th>
                  <th className="px-3 py-2.5 text-center" data-id="element-397" data-mp="3jdys8llgj1gl">Cant.</th>
                  <th className="px-3 py-2.5 text-right" data-id="element-398" data-mp="1wpb2qq4528i">Precio</th>
                  <th className="px-3 py-2.5" data-id="element-399" data-mp="1y0euqjk69zm">Método</th>
                  <th className="px-3 py-2.5 rounded-r-lg" data-id="element-400" data-mp="3tb3e0kbe4uwv">Canal</th>
                </tr>
              </thead>
              <tbody data-id="element-401" data-mp="awcd9eqo2c2m">
                {flatRows.map((r, idx) => {
              const mMeta = methodMeta(r.method);
              const cMeta = channelMeta(r.channel, r.table_label);
              return <tr key={`${r.payment_id}-${idx}`} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50" data-id="element-402" data-mp="kv1lefsrgi5o" data-mpi={idx}>
                      <td className="px-6 py-2.5 text-gray-700 tabular-nums whitespace-nowrap" data-id="element-403" data-mp="2mnpu34o6bxsu">
                        {fmtTime(r.created_at)}
                      </td>
                      <td className="px-3 py-2.5 text-gray-500 text-xs tabular-nums whitespace-nowrap" data-id="element-404" data-mp="x5j3t3yoq07d">
                        #
                        {r.order_number ? r.order_number.split('-').pop() : r.order_id.slice(0, 6)}
                      </td>
                      <td className="px-3 py-2.5" data-id="element-405" data-mp="se12gesr1xlv">
                        <div className="font-medium text-gray-900" data-id="element-406" data-mp="scpyoezb0pur">
                          {r.item_name}
                        </div>
                        {r.notes && <div className="text-[11px] text-gray-500 italic" data-id="element-407" data-mp="jfpekkj9pdx4">
                            {r.notes}
                          </div>}
                      </td>
                      <td className="px-3 py-2.5 text-center text-gray-700 tabular-nums" data-id="element-408" data-mp="9ggpa4zcknh3">
                        {r.quantity}
                      </td>
                      <td className="px-3 py-2.5 text-right font-bold text-gray-900 tabular-nums whitespace-nowrap" data-id="element-409" data-mp="23urdcb84bdlt">
                        {formatCurrency(r.line_total)}
                      </td>
                      <td className="px-3 py-2.5" data-id="element-410" data-mp="ml9nj2nxqmci">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-bold ${mMeta.chip}`} data-id="element-411" data-mp="mmkxjk7z4qc2">
                          <mMeta.Icon className="w-3 h-3" data-id="element-412" data-mp="cvq33skkqkfj" />
                          {mMeta.label}
                        </span>
                      </td>
                      <td className="px-3 py-2.5" data-id="element-413" data-mp="3v1ytpkzfyfu">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-bold ${cMeta.chip}`} data-id="element-414" data-mp="3xeg00ix24ru">
                          <cMeta.Icon className="w-3 h-3" data-id="element-415" data-mp="3q4cpwzq0vcas" />
                          {cMeta.label}
                        </span>
                      </td>
                    </tr>;
            })}
              </tbody>
              <tfoot data-id="element-416" data-mp="1vuxnhv7tjf8g">
                <tr className="border-t-2 border-gray-200 bg-amber-50/50" data-id="element-417" data-mp="1vtmn85dl0pm3">
                  <td colSpan={4} className="px-6 py-3 font-black text-gray-900" data-id="element-418" data-mp="20thalyy5ex2e">
                    TOTAL DEL DÍA
                  </td>
                  <td className="px-3 py-3 text-right font-black text-amber-600 text-base tabular-nums" data-id="element-419" data-mp="2ajaoj6lrjo9t">
                    {formatCurrency(totals.grand)}
                  </td>
                  <td colSpan={2} className="px-3 py-3 text-xs text-gray-500" data-id="element-420" data-mp="1cy6b85qp20dw">
                    {totals.paymentCount} cobros · {totals.itemCount} ítems
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>}

        {/* ─── Anulaciones y eliminaciones del turno ─── */}
        <div className="mt-8 pt-8 border-t border-gray-200" data-id="element-421" data-mp="2y9o6u5ejq8r7">
          <div className="flex items-center gap-2 mb-4" data-id="element-422" data-mp="2yb91dv0v1rp2">
            <Ban className="w-5 h-5 text-red-500" data-id="element-423" data-mp="3376k27zlja50" />
            <h3 className="text-lg font-black text-gray-900" data-id="element-424" data-mp="33a2e3l8bpeo6">
              Anulaciones y eliminaciones del turno
            </h3>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-4" data-id="element-425" data-mp="33714ohvqudkp">
            <div className="rounded-xl border border-red-100 bg-red-50 p-3" data-id="element-426" data-mp="3852pb78tikya">
              <p className="text-[10px] font-bold text-red-700 uppercase tracking-wider" data-id="element-427" data-mp="3875niek4jke3">
                Monto Anulado
              </p>
              <p className="text-xl font-black text-red-900 tabular-nums mt-1" data-id="element-428" data-mp="3h6akvhjy97oo">
                {formatCurrency(voidLogs.reduce((sum, log) => sum + Number(log.amount || 0), 0))}
              </p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-3" data-id="element-429" data-mp="3vux0zlhei3gy">
              <p className="text-[10px] font-bold text-gray-600 uppercase tracking-wider" data-id="element-430" data-mp="4om6672zhyy2">
                Eventos
              </p>
              <p className="text-xl font-black text-gray-900 tabular-nums mt-1" data-id="element-431" data-mp="8qz6gfyyp4ng">
                {voidLogs.length}
              </p>
            </div>
          </div>

          {voidLogs.length === 0 ? <div className="text-center py-6 px-4 text-gray-500 text-sm border border-dashed border-gray-200 rounded-xl bg-gray-50/50" data-id="element-432" data-mp="1p5ll5yr4gf84">
              <p className="font-medium" data-id="element-433" data-mp="1k94w92c3lwtz">
                No hay anulaciones registradas en este rango.
              </p>
            </div> : <div className="overflow-x-auto -mx-6 sm:mx-0" data-id="element-434" data-mp="2cuxt6vapqh8u">
              <table className="w-full text-sm text-left min-w-[600px]" data-id="element-435" data-mp="27x4lfxtrhf6g">
                <thead className="text-[11px] text-gray-500 uppercase bg-gray-50" data-id="element-436" data-mp="27yz5zb14f5a0">
                  <tr data-id="element-437" data-mp="234bhhc64aavf">
                    <th className="px-4 py-2 rounded-l-lg" data-id="element-438" data-mp="233tcnr5e2obw">Fecha</th>
                    <th className="px-3 py-2" data-id="element-439" data-mp="1y6jo0dptqgp4">Hora</th>
                    <th className="px-3 py-2" data-id="element-440" data-mp="2vmyyafe2z3qp">Acción</th>
                    <th className="px-3 py-2" data-id="element-441" data-mp="2vojsg3whgko1">Detalle</th>
                    <th className="px-3 py-2" data-id="element-442" data-mp="2qvgyvvrphb7u">Autorizado por</th>
                    <th className="px-3 py-2" data-id="element-443" data-mp="oy6nj8scs9eh">Motivo</th>
                    <th className="px-4 py-2 text-right rounded-r-lg" data-id="element-444" data-mp="oyq8yzh7pfx9">Monto</th>
                  </tr>
                </thead>
                <tbody data-id="element-445" data-mp="t1wdo2ooez1m">
                  {voidLogs.map(log => <tr key={log.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50" data-id="element-446" data-mp="f7vkrpkeo696">
                      <td className="px-4 py-2.5 text-gray-700 tabular-nums whitespace-nowrap" data-id="element-447" data-mp="59xvqzuq25fi">
                        {fmtDateCR(log.created_at)}
                      </td>
                      <td className="px-3 py-2.5 text-gray-700 tabular-nums whitespace-nowrap" data-id="element-448" data-mp="3sf6q5u3hjruu">
                        {fmtTimeCR(log.created_at)}
                      </td>
                      <td className="px-3 py-2.5" data-id="element-449" data-mp="1aq836q6s94y">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10px] font-bold ${log.action_type === 'cancel_order' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-orange-50 text-orange-700 border-orange-200'}`} data-id="element-450" data-mp="1qoljyoqevxta">
                          {log.action_type === 'cancel_order' ? 'Orden Completa' : 'Artículo'}
                        </span>
                      </td>
                      <td className="px-3 py-2.5" data-id="element-451" data-mp="29rdohel762mw">
                        <div className="font-medium text-gray-900" data-id="element-452" data-mp="2ogt9edy1rduo">
                          {log.action_type === 'cancel_order' ? `Orden #${log.order_id}` : log.detail}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-gray-700 font-medium" data-id="element-453" data-mp="2xff0ixhl4172">
                        {log.authorized_name}
                      </td>
                      <td className="px-3 py-2.5 text-gray-500 text-xs italic max-w-[200px] truncate" title={log.reason} data-id="element-454" data-mp="134rtiqx2o506">
                        {log.reason}
                      </td>
                      <td className="px-4 py-2.5 text-right font-bold text-red-600 tabular-nums whitespace-nowrap" data-id="element-455" data-mp="15idgb9xd9um8">
                        {formatCurrency(log.amount)}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>}
        </div>
      </CardContent>
    </Card>;
}